import { BackofficePromoPeriods } from '@diamond/shared/types';
import { getPromoPeriodValue } from '@diamond/sol-admin/utils';
import { format, subDays } from 'date-fns';
import { useCallback, useMemo } from 'react';

import { useCleanSearchParams } from './use-clean-searchparams';

/**
 * Hook to manage date filtering with URL parameters
 * @param options Configuration options for the date filter
 * @returns Object with date parameters, handlers, and formatted values
 */
export function useDateFilter(
  options = {
    fromField: 'order_date-from',
    toField: 'order_date-to',
    paramFromName: 'startdate',
    paramToName: 'enddate',
    defaultPeriod: '',
    resetPage: true,
  }
) {
  const { searchParams, setParams } = useCleanSearchParams();

  const dateParams = useMemo(() => {
    const fromDateParam = searchParams.get(options.paramFromName) || '';
    const fromDate = fromDateParam ? fromDateParam.split('-').join('/') : '';

    const toDateParam = searchParams.get(options.paramToName) || '';
    const toDate = toDateParam ? toDateParam.split('-').join('/') : '';

    return {
      [options.fromField]: fromDate,
      [options.toField]: toDate,
    };
  }, [
    searchParams,
    options.paramFromName,
    options.paramToName,
    options.fromField,
    options.toField,
  ]);

  /**
   * Handles date filter changes based on period selection
   * @param period The selected period (7d, 1m, 3m, 6m, 1y, custom, all)
   * @returns Object with fromDate and toDate strings
   */
  const handleDateFilter = useCallback((period: string) => {
    const today = new Date();
    const dateFormat = 'dd/MM/yyyy';
    let fromDate = '';
    let toDate = '';

    if (period !== 'all') {
      toDate = format(today, dateFormat);

      try {
        switch (period) {
          case '7d':
            fromDate = format(subDays(today, 7), dateFormat);
            break;
          case '1m':
            fromDate = format(subDays(today, 30), dateFormat);
            break;
          case '3m':
            fromDate = format(subDays(today, 90), dateFormat);
            break;
          case '6m':
            fromDate = format(subDays(today, 180), dateFormat);
            break;
          case '1y': {
            const oneYearAgo = subDays(today, 365);
            fromDate = format(oneYearAgo, dateFormat);
            break;
          }
          case 'custom':
            break;
          default:
            return { fromDate: '', toDate: '' };
        }
      } catch (error) {
        console.error('Error formatting date:', error);
        return { fromDate: '', toDate: '' };
      }
    }

    updateDateParams(fromDate, toDate);

    return { fromDate, toDate };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Updates URL parameters with the selected date range
   * @param fromDate Start date string in DD/MM/YYYY format
   * @param toDate End date string in DD/MM/YYYY format
   */
  const updateDateParams = useCallback(
    (fromDate: string, toDate: string) => {
      const paramsToSet: Record<string, string | undefined> = {};

      if (options.resetPage) {
        paramsToSet.page = '1';
      }

      if (fromDate && toDate) {
        paramsToSet[options.paramFromName] = fromDate.split('/').join('-');
        paramsToSet[options.paramToName] = toDate.split('/').join('-');
      } else {
        paramsToSet[options.paramFromName] = undefined;
        paramsToSet[options.paramToName] = undefined;
      }

      setParams(paramsToSet);
    },
    [setParams, options.paramFromName, options.paramToName, options.resetPage]
  );

  const promoPeriod = useMemo((): BackofficePromoPeriods => {
    const fromDate = dateParams[options.fromField];
    const toDate = dateParams[options.toField];

    return getPromoPeriodValue(fromDate, toDate);
  }, [dateParams, options.fromField, options.toField]);

  const apiDates = useMemo(() => {
    const fromDate = dateParams[options.fromField];
    const toDate = dateParams[options.toField];

    if (!fromDate || !toDate) {
      return { period_from: '', period_to: '' };
    }

    /**
     * Formats date string to YYYY-MM-DD format for API requests
     * @param dateStr Date string in DD/MM/YYYY format
     * @returns Formatted date string in YYYY-MM-DD format
     */
    const formatDateForApi = (dateStr: string): string => {
      try {
        if (!dateStr || typeof dateStr !== 'string') return '';

        const parts = dateStr.split('/');
        if (parts.length !== 3) return '';

        const day = parts[0];
        const month = parts[1];
        const year = parts[2];

        const fullYear = year.length === 2 ? `20${year}` : year;

        return `${fullYear}-${month}-${day}`;
      } catch (error) {
        console.error('Error formatting date for API:', error);
        return '';
      }
    };

    return {
      period_from: formatDateForApi(fromDate),
      period_to: formatDateForApi(toDate),
    };
  }, [dateParams, options.fromField, options.toField]);

  return {
    dateParams,
    handleDateFilter,
    updateDateParams,
    promoPeriod,
    apiDates,
  };
}
